import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { recoverValuesFromSearchParams } from '../Crud/useFilter';
import { resolveListOrder } from './listOrder.utils';
import { OrderBy, OrderType } from './useList.types';

type ListOrderProps = {
  addFilters: (filtersToAdd: any) => void;
  defaultOrder?: OrderBy;
};

export type ListOrder = {
  orders: OrderBy;
  handleRequestSort: (property: string) => void;
};

export type MuiOrder = 'asc' | 'desc';

export enum SortEnum {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const getMuiOrderDirection = (direction: string): MuiOrder | undefined => {
  if (!direction) {
    return;
  }

  return direction.toLocaleLowerCase() === 'asc' ? 'asc' : 'desc';
};

const useListOrder = ({ addFilters, defaultOrder }: ListOrderProps): ListOrder => {
  const location = useLocation();

  const orders: OrderBy = useMemo(() => {
    const params = recoverValuesFromSearchParams(location.search);
    return resolveListOrder({ order: params.order as OrderBy, defaultOrder });
  }, [location, defaultOrder]);

  const handleRequestSort = useCallback(
    (property: string) => {
      const orderType: OrderType =
        (orders[property] || SortEnum.DESC).toUpperCase() === SortEnum.ASC
          ? SortEnum.DESC
          : SortEnum.ASC;
      const newOrderBy = { [property]: orderType };
      const orderFields = { order: newOrderBy };
      addFilters(orderFields);
    },
    [orders, addFilters],
  );

  return {
    orders,
    handleRequestSort,
  };
};

export default useListOrder;
